import { useMemo, useState } from "react";
import { ShieldCheck } from "lucide-react";
import { ToolLayout, TwoCol } from "@/components/tools/ToolLayout";
import { SectionCard, Field, MoneyInput, SliderRow, Segmented, StatCard, InfoRow, compactINR } from "@/components/tools/kit";
import { termInsurance } from "@/lib/calculators";

export default function TermInsurance() {
  const [method, setMethod] = useState("expense");
  const [age, setAge] = useState(35);
  const [monthlyExpenses, setExp] = useState(60000);
  const [annualIncome, setIncome] = useState(1800000);
  const [liabilities, setLiab] = useState(5000000);
  const [existingCover, setExisting] = useState(2000000);
  const [existingSavings, setSavings] = useState(3000000);
  const [inflation, setInflation] = useState(6);
  const r = useMemo(() => termInsurance({ age, monthlyExpenses, annualIncome, liabilities, existingCover, existingSavings, inflation, method }),
    [age, monthlyExpenses, annualIncome, liabilities, existingCover, existingSavings, inflation, method]);
  return (
    <ToolLayout title="Term Insurance Calculator" subtitle="How much life cover your family truly needs" icon={ShieldCheck} accent="#2D6A4F">
      <TwoCol
        left={
          <SectionCard title="Your details">
            <div className="space-y-5">
              <Field label="Method">
                <Segmented testid="ti-method" value={method} onChange={setMethod} layoutId="ti-method-pill"
                  options={[{ id: "expense", label: "Expense replacement" }, { id: "income", label: "Income multiple (10x)" }]} />
              </Field>
              <Field label="Your age" hint={age}><SliderRow testid="ti-age" value={age} min={18} max={60} suffix=" yr" onChange={setAge} /></Field>
              {method === "expense"
                ? <Field label="Monthly household expenses"><MoneyInput testid="ti-expenses" value={monthlyExpenses} onChange={setExp} helper={compactINR(monthlyExpenses) + " / month"} /></Field>
                : <Field label="Annual income"><MoneyInput testid="ti-income" value={annualIncome} onChange={setIncome} helper={compactINR(annualIncome) + " / year"} /></Field>}
              <Field label="Outstanding liabilities (loans)"><MoneyInput testid="ti-liabilities" value={liabilities} onChange={setLiab} helper={compactINR(liabilities)} /></Field>
              <Field label="Existing life cover"><MoneyInput testid="ti-existing" value={existingCover} onChange={setExisting} helper={compactINR(existingCover)} /></Field>
              <Field label="Existing savings / investments"><MoneyInput testid="ti-savings" value={existingSavings} onChange={setSavings} helper={compactINR(existingSavings)} /></Field>
              {method === "expense" && <Field label="Inflation (p.a.)" hint={inflation + "%"}><SliderRow testid="ti-inflation" value={inflation} min={0} max={12} step={0.5} suffix="%" onChange={setInflation} /></Field>}
            </div>
          </SectionCard>
        }
        right={
          <>
            <StatCard testid="ti-recommended" label="Recommended Life Cover" value={compactINR(r.recommended)} accent="#2D6A4F" big />
            <SectionCard title="How we got here">
              <InfoRow label={method === "expense" ? `Family needs (${r.yearsLeft} yrs of expenses)` : "Income cover (10x annual)"} value={compactINR(r.need)} strong />
              <InfoRow label="+ Liabilities to clear" value={compactINR(liabilities)} />
              <InfoRow label="− Existing cover" value={compactINR(existingCover)} />
              <InfoRow label="− Existing savings" value={compactINR(existingSavings)} />
              <InfoRow label="= Recommended cover" value={compactINR(r.recommended)} strong />
              <p className="text-xs text-[#8A9790] mt-3">A pure term plan is the cheapest way to secure this cover. Review it after major life events.</p>
            </SectionCard>
          </>
        }
      />
    </ToolLayout>
  );
}
